import { Helmet } from "react-helmet";
import { SideBar } from "../side-bar/SideBar";
import CstAdminHeader from "./admin-access-header/CstAdminHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import { api, getToken } from "../../../services/apis/auth-api/apiUrl";
import { GET_REVENUE_SUMMARY } from "../../../config/config";
import { months } from "../../../utils/time.date.utlis";

interface annualSummaryProps {
    recruiter_name: string,
    onboarded_rev: number,
    onboarded_ftes: number,
    offer_accepted_rev: number,
    offer_accepted_ftes: number,
    for_offer_rev: number,
    for_offer_ftes: number,
    for_final_rev: number,
    for_final_ftes: number
}

interface quarterSummaryProps {
    recruiter_name: string,
    month_1_rev : number,
    month_1_ftes : number,
    month_2_rev : number,
    month_2_ftes : number,
    month_3_rev : number,
    month_3_ftes : number
    month_1_offer_accepted : number,
    month_1_offer_accepted_ftes : number,
    month_2_offer_accepted : number,
    month_2_offer_accepted_ftes : number,
    month_3_offer_accepted : number,
    month_3_offer_accepted_ftes : number,
    month_1_for_offer : number,
    month_1_for_offer_ftes : number,
    month_2_for_offer : number,
    month_2_for_offer_ftes : number,
    month_3_for_offer : number,
    month_3_for_offer_ftes : number
}

const RevenueSummary = () => {
    const date = new Date();
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();
    const [year, setYear] = useState<number>(currentYear);
    const [quarter, setQuarter] = useState<number>(Math.floor(currentMonth / 3) + 1);
    const [annualSummary, setAnnualSummary] = useState<annualSummaryProps[] | null>(null);
    const [quarterSummary, setQuarterSummary] = useState<quarterSummaryProps[] | null>(null);
    const [quarterMonths, setQuarterMonths] = useState<number[]>([1,2,3]);
    const indx = (quarterMonths[0] - 1) === currentMonth ? 0 :
        (quarterMonths[1] - 1) === currentMonth ? 1 : 2
    const [selectedMonthIndx, setSelectedMonthIndx] = useState<number>(indx);

    const incrementYear = () => { 
        setYear(prevYear => prevYear + 1)
        setAnnualSummary(null);
        setQuarterSummary(null);
    };

    const decrementYear = () => {
        setYear(prevYear => prevYear - 1)
        setAnnualSummary(null);
        setQuarterSummary(null);
    };

    const totalQuarterRev = () => {
        if(!quarterSummary) return "0.00";
        const month_1 = quarterSummary.reduce((sum, rev) => sum + Number(rev[`month_${Number(selectedMonthIndx + 1)}_rev` as keyof quarterSummaryProps] ?? 0), 0);
        const month_2 = quarterSummary.reduce((sum, rev) => sum + Number(rev[`month_${Number(selectedMonthIndx + 1)}_offer_accepted` as keyof quarterSummaryProps] ?? 0), 0);
        const month_3 = quarterSummary.reduce((sum, rev) => sum + Number(rev[`month_${Number(selectedMonthIndx + 1)}_for_offer` as keyof quarterSummaryProps] ?? 0), 0);
        return month_1 + month_2 + month_3;
    }

    const totalQuarterRevOnboarded = () => {
        if(!quarterSummary) return "0.00";
        const month_1 = quarterSummary.reduce((sum, rev) => sum + rev.month_1_rev, 0);
        const month_2 = quarterSummary.reduce((sum, rev) => sum + rev.month_2_rev, 0);
        const month_3 = quarterSummary.reduce((sum, rev) => sum + rev.month_3_rev, 0);
        return month_1 + month_2 + month_3;
    }

    const totalQuarterFte = () => {
        if(!quarterSummary) return 0;
        const month_1 = quarterSummary.reduce((sum, fte) => sum + Number(fte[`month_${Number(selectedMonthIndx + 1)}_ftes` as keyof quarterSummaryProps] ?? 0), 0);
        const month_2 = quarterSummary.reduce((sum, fte) => sum + Number(fte[`month_${Number(selectedMonthIndx + 1)}_offer_accepted_ftes` as keyof quarterSummaryProps] ?? 0), 0);
        const month_3 = quarterSummary.reduce((sum, fte) => sum + Number(fte[`month_${Number(selectedMonthIndx + 1)}_for_offer_ftes` as keyof quarterSummaryProps] ?? 0), 0);
        return month_1 + month_2 + month_3;
    }

    const totalQuarterFteOnboarded = () => {
        if(!quarterSummary) return 0;
        const month_1 = quarterSummary.reduce((sum, fte) => sum + fte.month_1_ftes, 0);
        const month_2 = quarterSummary.reduce((sum, fte) => sum + fte.month_2_ftes, 0);
        const month_3 = quarterSummary.reduce((sum, fte) => sum + fte.month_3_ftes, 0);
        return month_1 + month_2 + month_3;
    }

    useEffect(()=>{
        const setSummaries = async() => {
            setQuarterSummary(null);
            try{
                const summary = await api.get(
                    GET_REVENUE_SUMMARY+year+'/'+quarter,{
                        headers : {'Authorization' : 'Bearer '+getToken()}
                    }
                );
                const annual_summary = summary.data.annual_rev_summary as annualSummaryProps[];
                const quarter_summary = summary.data.quarter_summary as quarterSummaryProps[]; 
                const quarter_months = summary.data.quarter_months_arr as number[];

                const sorted_annual_summary = annual_summary.sort((a, b) => a.recruiter_name.localeCompare(b.recruiter_name))
                setAnnualSummary(sorted_annual_summary);
                const sorted_quarter_summary = quarter_summary.sort((a, b) => a.recruiter_name.localeCompare(b.recruiter_name))
                setQuarterSummary(sorted_quarter_summary);
                setQuarterMonths(quarter_months);
            }catch(e){}
        }
        setSummaries();
    },[year, quarter])

    return(
        <div className="content flex">
             <Helmet>
                <title>Revenue Summary</title>
            </Helmet>

            <SideBar />

            <div className='main-content'>
                <div>
                    <h1 className="uppercase">
                        Client services tracker
                    </h1>
                </div>
                <div className="flex flex-col my-4">
                    {/* Client cards */}
                    <div className="bg-white w-full flex p-2">
                        <CstAdminHeader />
                    </div>
                   
                    <div className="bg-white p-2 shadow w-full relative">
                        <div>
                            <h2>Revenue Summary</h2>
                        </div>
                        <div className="flex my-2">
                            <div className="ms-auto flex gap-4 items-center mx-2">
                                <div className="flex gap-4 text-xl text-gray-700">
                                    <div>
                                        <button className="hover:text-orange-500" onClick={decrementYear}>
                                            <FontAwesomeIcon icon={faAngleLeft} />
                                        </button>
                                    </div>
                                    <div>
                                        {year ? year : currentYear}
                                    </div>
                                    <div>
                                        <button className="hover:text-orange-500" onClick={incrementYear}>
                                            <FontAwesomeIcon icon={faAngleRight} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                       <div className="table-container">
                            <table className="w-full h-full border">
                                <thead className="table-fixed-header border">
                                    <tr className="bg-gray-200 text-sm">
                                        <th className='table-headers font-medium' colSpan={9}>{year} Revenue Hires</th>
                                    </tr>
                                    <tr className="bg-gray-100 text-sm">
                                        <th className='table-headers font-medium'></th>
                                        <th className='table-headers font-medium bg-orange-200' colSpan={2}>Onboarded Hires</th>
                                        <th className='table-headers font-medium bg-green-200' colSpan={2}>Offer Accepted</th>
                                        <th className='table-headers font-medium bg-yellow-200' colSpan={2}>For Offer / Done FI</th>
                                        <th className='table-headers font-medium bg-blue-200' colSpan={2}>For Final / HM Interview</th>
                                    </tr>
                                    <tr className="bg-gray-100 text-sm">
                                        <th className='table-headers font-medium'>Recruiters</th>
                                        <th className='table-headers font-medium bg-orange-200'>Revenue</th>
                                        <th className='table-headers font-medium bg-orange-200'>FTEs</th>
                                        <th className='table-headers font-medium bg-green-200'>Revenue</th>
                                        <th className='table-headers font-medium bg-green-200'>FTEs</th>
                                        <th className='table-headers font-medium bg-yellow-200'>Revenue</th>
                                        <th className='table-headers font-medium bg-yellow-200'>FTEs</th>
                                        <th className='table-headers font-medium bg-blue-200'>Revenue</th>
                                        <th className='table-headers font-medium bg-blue-200'>FTEs</th>
                                    </tr>
                                </thead>
                                <tbody className="text-center">
                                    {annualSummary ? annualSummary.map(annual => (
                                        <tr className="hover:bg-gray-300">
                                            <td className='table-body px-2'>{annual.recruiter_name}</td>
                                            <td className='table-body px-2'>{parseFloat(annual.onboarded_rev.toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</td>
                                            <td className='table-body px-2'>{annual.onboarded_ftes}</td>
                                            <td className='table-body px-2'>{parseFloat(annual.offer_accepted_rev.toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</td>
                                            <td className='table-body px-2'>{annual.offer_accepted_ftes}</td>
                                            <td className='table-body px-2'>{parseFloat(annual.for_offer_rev.toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</td>
                                            <td className='table-body px-2'>{annual.for_offer_ftes}</td>
                                            <td className='table-body px-2'>{parseFloat(annual.for_final_rev.toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</td>
                                            <td className='table-body px-2'>{annual.for_final_ftes}</td>
                                        </tr>
                                    )) : Array.from({length : 3}).map((_, index) => (
                                        <tr key={index}>
                                            {[...Array(9)].map((_, colIndex) => (
                                                <td key={colIndex} className='table-body'>
                                                    <div className="table-loading bg-gray-200"></div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-fixed-footer text-center border text-gray-600">
                                    <tr className="border">
                                        <th className="border">Total</th>
                                        <th className="border">{annualSummary && parseFloat(annualSummary.reduce((sum, rev) => sum + rev.onboarded_rev, 0).toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</th>
                                        <th className="border">{annualSummary && annualSummary.reduce((sum, rev) => sum + rev.onboarded_ftes, 0)}</th>
                                        <th className="border">{annualSummary && parseFloat(annualSummary.reduce((sum, rev) => sum + rev.offer_accepted_rev, 0).toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</th>
                                        <th className="border">{annualSummary && annualSummary.reduce((sum, rev) => sum + rev.offer_accepted_ftes, 0)}</th>
                                        <th className="border">{annualSummary && parseFloat(annualSummary.reduce((sum, rev) => sum + rev.for_offer_rev, 0).toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</th>
                                        <th className="border">{annualSummary && annualSummary.reduce((sum, rev) => sum + rev.for_offer_ftes, 0)}</th>
                                        <th className="border">{annualSummary && parseFloat(annualSummary.reduce((sum, rev) => sum + rev.for_final_rev, 0).toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</th>
                                        <th className="border">{annualSummary && annualSummary.reduce((sum, rev) => sum + rev.for_final_ftes, 0)}</th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        <div className="flex mt-4 my-2">
                            <div className="ms-auto flex gap-4 items-center mx-2">
                                <div>
                                    <select className="text-box" onChange={(e)=>setQuarter(parseInt(e.currentTarget.value))}>
                                        <option selected={quarter === 1 ? true : false} value="1">Quarter 1</option>
                                        <option selected={quarter === 2 ? true : false} value="2">Quarter 2</option>
                                        <option selected={quarter === 3 ? true : false} value="3">Quarter 3</option>
                                        <option selected={quarter === 4 ? true : false} value="4">Quarter 4</option>
                                    </select>
                                </div>
                                <div>
                                    <select className="text-box" onChange={(e)=>setSelectedMonthIndx(parseInt(e.currentTarget.value))}>
                                        <option selected={selectedMonthIndx === 0 ? true : false} value="0">{months[quarterMonths[0] - 1]}</option>
                                        <option selected={selectedMonthIndx === 1 ? true : false} value="1">{months[quarterMonths[1] - 1]}</option>
                                        <option selected={selectedMonthIndx === 2 ? true : false} value="2">{months[quarterMonths[2] - 1]}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        {/* ?Monthly Revenue */}
                        <div className="table-container">
                            <table className="w-full h-full border">
                                <thead className="table-fixed-header border">
                                    <tr className="bg-gray-200 text-sm">
                                        <th className='table-headers font-medium' colSpan={9}>{months[quarterMonths[selectedMonthIndx] - 1]} {year} Revenue Movement</th>
                                    </tr>
                                    <tr className="bg-gray-100 text-sm">
                                        <th className='table-headers font-medium'></th>
                                        <th className='table-headers font-medium' colSpan={2}>Onboarded</th>
                                        <th className='table-headers font-medium' colSpan={2}>Offer Accepted</th>
                                        <th className='table-headers font-medium' colSpan={2}>For Offer / Done FI</th>
                                        <th className='table-headers font-medium' colSpan={2}>Revenue Summary</th>
                                    </tr>
                                    <tr className="bg-gray-100 text-sm">
                                        <th className='table-headers font-medium'>Recruiters</th>
                                        <th className='table-headers font-medium'>Revenue</th>
                                        <th className='table-headers font-medium'>FTEs</th>
                                        <th className='table-headers font-medium'>Revenue</th>
                                        <th className='table-headers font-medium'>FTEs</th>
                                        <th className='table-headers font-medium'>Revenue</th>
                                        <th className='table-headers font-medium'>FTEs</th>
                                        <th className='table-headers font-medium'>Total Revenue</th>
                                        <th className='table-headers font-medium'>Total FTEs</th>
                                    </tr>
                                </thead>
                                <tbody className="text-center">
                                    {quarterSummary ? quarterSummary.map(quarter => (
                                        <tr className="hover:bg-gray-300">
                                            <td className='table-body px-2'>{quarter.recruiter_name}</td>
                                            <td className='table-body px-2'>{parseFloat(quarter[`month_${selectedMonthIndx + 1}_rev` as keyof quarterSummaryProps]?.toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</td>
                                            <td className='table-body px-2'>{quarter[`month_${selectedMonthIndx + 1}_ftes` as keyof quarterSummaryProps]}</td>
                                            <td className='table-body px-2'>{parseFloat(quarter[`month_${selectedMonthIndx + 1}_offer_accepted` as keyof quarterSummaryProps]?.toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</td>
                                            <td className='table-body px-2'>{quarter[`month_${selectedMonthIndx + 1}_offer_accepted_ftes` as keyof quarterSummaryProps]}</td>
                                            <td className='table-body px-2'>{parseFloat(quarter[`month_${selectedMonthIndx + 1}_for_offer` as keyof quarterSummaryProps].toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</td>
                                            <td className='table-body px-2'>{quarter[`month_${selectedMonthIndx + 1}_for_offer_ftes` as keyof quarterSummaryProps]}</td>
                                            <td className='table-body px-2'>
                                                {(
                                                    Number(quarter[`month_${Number(selectedMonthIndx) + 1}_rev` as keyof quarterSummaryProps] ?? 0) +
                                                    Number(quarter[`month_${Number(selectedMonthIndx) + 1}_offer_accepted` as keyof quarterSummaryProps] ?? 0) +
                                                    Number(quarter[`month_${Number(selectedMonthIndx) + 1}_for_offer` as keyof quarterSummaryProps] ?? 0)
                                                    ).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 
                                                
                                                })}
                                            </td>
                                            <td className='table-body px-2'>
                                                { 
                                                Number(quarter[`month_${Number(selectedMonthIndx) + 1}_ftes` as keyof quarterSummaryProps] ?? 0) +
                                                Number(quarter[`month_${Number(selectedMonthIndx) + 1}_offer_accepted_ftes` as keyof quarterSummaryProps] ?? 0) +
                                                Number(quarter[`month_${Number(selectedMonthIndx) + 1}_for_offer_ftes` as keyof quarterSummaryProps] ?? 0)
                                                }
                                            </td>
                                        </tr>
                                    )) : Array.from({length : 3}).map((_, index) => (
                                        <tr key={index}>
                                            {[...Array(9)].map((_, colIndex) => (
                                                <td key={colIndex} className='table-body'>
                                                    <div className="table-loading bg-gray-200"></div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-fixed-footer text-center border text-gray-600">
                                    <tr className="border">
                                        <th className="border">Total</th>
                                        <th className="border">
                                            {quarterSummary && 
                                                quarterSummary
                                                .reduce(
                                                (sum, rev) => 
                                                    sum + Number(rev[`month_${Number(selectedMonthIndx) + 1}_rev` as keyof quarterSummaryProps] ?? 0),
                                                0
                                                )
                                                .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                            }
                                        </th>
                                        <th className="border">
                                            {quarterSummary &&
                                                quarterSummary.reduce(
                                                    (sum, rev) =>
                                                    sum + Number(rev[`month_${Number(selectedMonthIndx) + 1}_ftes` as keyof quarterSummaryProps] ?? 0),
                                                    0
                                                )
                                            }
                                        </th>
                                        <th className="border">
                                            {quarterSummary && 
                                                quarterSummary
                                                .reduce(
                                                (sum, rev) => 
                                                    sum + Number(rev[`month_${Number(selectedMonthIndx) + 1}_offer_accepted` as keyof quarterSummaryProps] ?? 0),
                                                0
                                                )
                                                .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                            }
                                        </th>
                                        <th className="border">
                                            {quarterSummary &&
                                                quarterSummary.reduce(
                                                    (sum, rev) =>
                                                    sum + Number(rev[`month_${Number(selectedMonthIndx) + 1}_offer_accepted_ftes` as keyof quarterSummaryProps] ?? 0),
                                                    0
                                                )
                                            }
                                        </th>
                                        <th className="border">
                                            {quarterSummary && 
                                                quarterSummary
                                                .reduce(
                                                (sum, rev) => 
                                                    sum + Number(rev[`month_${Number(selectedMonthIndx) + 1}_for_offer` as keyof quarterSummaryProps] ?? 0),
                                                0
                                                )
                                                .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                            }
                                        </th>
                                        <th className="border">
                                            {quarterSummary &&
                                                quarterSummary.reduce(
                                                    (sum, rev) =>
                                                    sum + Number(rev[`month_${Number(selectedMonthIndx) + 1}_for_offer_ftes` as keyof quarterSummaryProps] ?? 0),
                                                    0
                                                )
                                            }
                                        </th>
                                        <th className="border">{parseFloat(totalQuarterRev().toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</th>
                                        <th className="border">{totalQuarterFte()}</th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        {/* Quarterly Revenue */}
                        <div className="table-container">
                            <table className="w-full h-full border">
                                <thead className="table-fixed-header border">
                                    <tr className="bg-gray-200 text-sm">
                                        <th className='table-headers font-medium' colSpan={9}>{year} Quarter {quarter} Revenue (Onboarded only)</th>
                                    </tr>
                                    <tr className="bg-gray-100 text-sm">
                                        <th className='table-headers font-medium'></th>
                                        <th className='table-headers font-medium' colSpan={2}>{months[quarterMonths[0] - 1]}</th>
                                        <th className='table-headers font-medium' colSpan={2}>{months[quarterMonths[1] - 1]}</th>
                                        <th className='table-headers font-medium' colSpan={2}>{months[quarterMonths[2] - 1]}</th>
                                        <th className='table-headers font-medium' colSpan={2}>Revenue Summary</th>
                                    </tr>
                                    <tr className="bg-gray-100 text-sm">
                                        <th className='table-headers font-medium'>Recruiters</th>
                                        <th className='table-headers font-medium'>Revenue</th>
                                        <th className='table-headers font-medium'>FTEs</th>
                                        <th className='table-headers font-medium'>Revenue</th>
                                        <th className='table-headers font-medium'>FTEs</th>
                                        <th className='table-headers font-medium'>Revenue</th>
                                        <th className='table-headers font-medium'>FTEs</th>
                                        <th className='table-headers font-medium'>Total Revenue</th>
                                        <th className='table-headers font-medium'>Total FTEs</th>
                                    </tr>
                                </thead>
                                <tbody className="text-center">
                                    {quarterSummary ? quarterSummary.map(quarter => (
                                        <tr className="hover:bg-gray-300">
                                            <td className='table-body px-2'>{quarter.recruiter_name}</td>
                                            <td className='table-body px-2'>{parseFloat(quarter.month_1_rev.toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</td>
                                            <td className='table-body px-2'>{quarter.month_1_ftes}</td>
                                            <td className='table-body px-2'>{parseFloat(quarter.month_2_rev.toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</td>
                                            <td className='table-body px-2'>{quarter.month_2_ftes}</td>
                                            <td className='table-body px-2'>{parseFloat(quarter.month_3_rev.toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</td>
                                            <td className='table-body px-2'>{quarter.month_3_ftes}</td>
                                            <td className='table-body px-2'>{parseFloat((quarter.month_1_rev + quarter.month_2_rev + quarter.month_3_rev).toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</td>
                                            <td className='table-body px-2'>{quarter.month_1_ftes + quarter.month_2_ftes + quarter.month_3_ftes}</td>
                                        </tr>
                                    )) : Array.from({length : 3}).map((_, index) => (
                                        <tr key={index}>
                                            {[...Array(9)].map((_, colIndex) => (
                                                <td key={colIndex} className='table-body'>
                                                    <div className="table-loading bg-gray-200"></div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-fixed-footer text-center border text-gray-600">
                                    <tr className="border">
                                        <th className="border">Total</th>
                                        <th className="border">{quarterSummary && parseFloat(quarterSummary.reduce((sum, rev) => sum + rev.month_1_rev, 0).toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</th>
                                        <th className="border">{quarterSummary && quarterSummary.reduce((sum, rev) => sum + rev.month_1_ftes, 0)}</th>
                                        <th className="border">{quarterSummary && parseFloat(quarterSummary.reduce((sum, rev) => sum + rev.month_2_rev, 0).toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</th>
                                        <th className="border">{quarterSummary && quarterSummary.reduce((sum, rev) => sum + rev.month_2_ftes, 0)}</th>
                                        <th className="border">{quarterSummary && parseFloat(quarterSummary.reduce((sum, rev) => sum + rev.month_3_rev, 0).toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</th>
                                        <th className="border">{quarterSummary && quarterSummary.reduce((sum, rev) => sum + rev.month_3_ftes, 0)}</th>
                                        <th className="border">{parseFloat(totalQuarterRevOnboarded().toString()).toLocaleString("en-US", {minimumFractionDigits : 2, maximumFractionDigits : 2})}</th>
                                        <th className="border">{totalQuarterFteOnboarded()}</th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default RevenueSummary;